import type { StaffSubRoleType } from '@billx/types';
import {
  ACCESS_ROLE_PRIORITY,
  getAccessibleActions,
  getAccessiblePages,
  hasActionAccess,
  hasPageAccess,
  hasPermission,
  type Permission,
} from '@billx/utils';

import { useCurrentAccessRole } from './accessRoles/useCurrentAccessRole';

export const useAccessControl = () => {
  const {
    currentAccessRole,
    isLoading,
    error: accessRoleError,
  } = useCurrentAccessRole();

  const { role, subRoles = [] } = currentAccessRole ?? {};
  const error = accessRoleError;

  const canAccessPage = (pageKey: string): boolean => {
    if (!role) return false;
    return hasPageAccess(role, subRoles ?? [], pageKey);
  };

  const canPerformAction = (actionKey: string): boolean => {
    if (!role) return false;
    return hasActionAccess(role, subRoles ?? [], actionKey);
  };

  const hasPermissionTo = (permission: Permission): boolean => {
    if (!role) return false;
    return hasPermission(role, subRoles, permission);
  };

  const getAccessiblePagesList = (): string[] => {
    if (!role) return [];
    return getAccessiblePages(role, subRoles);
  };

  const getAccessibleActionsList = (): string[] => {
    if (!role) return [];
    return getAccessibleActions(role, subRoles);
  };

  const hasRole = (needsRole: string): boolean => {
    return role === needsRole;
  };

  const hasSubRole = (subRole: StaffSubRoleType): boolean => {
    return subRoles.includes(subRole);
  };

  const isStaffWithSubRole = (subRole: StaffSubRoleType): boolean => {
    return hasRole('staff') && hasSubRole(subRole);
  };

  const getPrimaryRole = (): string => {
    if (!role) return '';

    return Object.keys(ACCESS_ROLE_PRIORITY).reduce((primary, current) => {
      const currentPriority =
        ACCESS_ROLE_PRIORITY[current as keyof typeof ACCESS_ROLE_PRIORITY] ?? 0;
      const primaryPriority =
        ACCESS_ROLE_PRIORITY[primary as keyof typeof ACCESS_ROLE_PRIORITY] ?? 0;

      return currentPriority < primaryPriority ? current : primary;
    });
  };

  const getSubRoles = (): StaffSubRoleType[] => {
    return subRoles;
  };

  return {
    isLoading,
    error,
    canAccessPage,
    canPerformAction,
    hasPermissionTo,
    hasRole,
    hasSubRole,
    isStaffWithSubRole,
    role,
    subRoles,
    getPrimaryRole,
    getSubRoles,
    getAccessiblePagesList,
    getAccessibleActionsList,
  };
};
